import { h, Fragment } from 'preact';
import { useEffect, useMemo, useState, useRef } from 'preact/hooks';
import { getBundleProducts } from 'utils/storefront/get-bundle-products';
import { trackPDPToggleClick, trackPDPAddToBag, trackPDPShippingClick } from 'utils/tracking';
import { formatMoney } from 'utils/storefront/format-resource';
import useIsInViewport from 'utils/isInViewPort';
import { withProductContext } from './product-context';
import BundleChild from './product-bundle-child';
import Reviews from './yotpo-reviews';
import ProductQuantity from './product-quantity';
import ProductDetails from './product-details';
import FlyoutPanel from './details-with-flyout-panel';
import { ShopPay } from './shop-pay';
import ProductAppBlocks from './product-app-blocks';

const Price = ({ variant, value }) => (
  <div className="mb-3.5">
    {value && value.amount > variant.price.amount ? (
      <>
        {variant.price && (
          <span className="text-red inline-block text-headline-tag mr-2">{formatMoney(variant.price)}</span>
        )}
        {value && value.amount > variant.price.amount && (
          <span className="text-headline-tag mr-1">
            ({formatMoney({ amount: value.amount })} {window.TMW.Strings.product.value})
          </span>
        )}
      </>
    ) : (
      <>
        <span className="text-headline-tag mr-1">{formatMoney(variant.price)}</span>
      </>
    )}
  </div>
);

const BundleChildren = ({ bundle, openFlyout }) => (
  <>
    <div className="flex items-center justify-between bg-pantone-8 px-5 h-16">
      <span className="flex items-center text-headline-tag">
        <span>{window.TMW.Strings.bundle.bundleTitle}</span>
        <span>&nbsp;({bundle?.products?.length})</span>
      </span>

      <button
        className="flyout-trigger flyout-trigger--1 underline text-body-2 -mt-1"
        tabIndex={0}
        title={window.TMW.Strings.product.fullList}
        type="button"
        onClick={openFlyout}
      >
        {window.TMW.Strings.product.fullList}
      </button>
    </div>
    <div className="max-h-[431px] overflow-auto mb-5">
      {bundle?.products?.map((product) => (
        <BundleChild key={product.id} product={product} />
      ))}
    </div>
  </>
);

const AddToCartSticky = ({ children }) => (
  <div className="fixed bottom-0 left-0 right-0 z-50 bg-white atc-wrapper">{children}</div>
);

const AddToCartButton = ({ bundle, product, variant, qty }) => {
  const handleAddToCart = (e) => {
    e.preventDefault();

    const addToCartEvent = new CustomEvent('cart:add', {
      detail: {
        payload: window.getBundlePayload(bundle, product, variant, qty),
      },
    });
    document.dispatchEvent(addToCartEvent);

    trackPDPAddToBag({
      item: product.title,
      itemId: variant.sku,
      quantity: qty,
    });
  };

  return (
    <button
      type="submit"
      className="atc-button button button--primary w-full h-[50px]"
      disabled={!bundle.available}
      onClick={(e) => handleAddToCart(e)}
    >
      {bundle.available ? window.TMW.Strings.product.addToCart : window.TMW.Strings.product.outOfStock}
    </button>
  );
};

const Shipping = ({ freeShipping: { freeShippingText, standardShipping } }) => {
  const freeShippingTitle = standardShipping
    ? window.TMW.Strings.product.standard_shipping
    : window.TMW.Strings.product.shipping_and_returns;

  const handleShippingFlyoutPanel = () => {
    trackPDPShippingClick(freeShippingTitle);
    const openShippingFlyoutPanel = new CustomEvent('shippingFlyoutPanel:open', {});
    document.dispatchEvent(openShippingFlyoutPanel);
  };

  return (
    <div className="flex items-center gap-2 mt-2">
      {freeShippingText && <p className="text-body-2">{freeShippingText}</p>} &bull;
      <button
        className="text-body-2 underline"
        type="button"
        onClick={handleShippingFlyoutPanel}
        tabIndex={0}
        aria-label={freeShippingTitle}
        title={freeShippingTitle}
      >
        {freeShippingTitle}
      </button>
    </div>
  );
};

const KeyDetailsWithFlyout = ({ detailsFlyout }) => <FlyoutPanel detailsFlyout={detailsFlyout} />;

const MaxOrderMessage = ({ maxOrder, errorMessage, quantity }) =>
  maxOrder && quantity >= maxOrder ? <p className="text-body-2 text-red mb-1.5">{errorMessage}</p> : null;

const BundleForm = ({ product, layout, variant, freeShipping, detailsFlyout, maxOrderQTY }) => {
  const atcRef = useRef(null);
  const isMobile = layout.bps.below.sm.matches;
  const isInViewPort = useIsInViewport(atcRef);
  const [bundle, setBundle] = useState(null);
  const [qty, setQty] = useState(1);
  const { openFlyout } = detailsFlyout;

  const title = useMemo(() => <h1 className="h2 tracking-tighter mb-1 lg:mb-2.5">{product.title}</h1>, [product.title]);

  const price = useMemo(() => <Price variant={variant} value={bundle?.value} />, [bundle]);

  const subtitle = useMemo(() => {
    if (product.subtitle) {
      const productSubtitleLowercase = product.subtitle.toLowerCase();
      const capitalizedSubtitle = productSubtitleLowercase.charAt(0).toUpperCase() + productSubtitleLowercase.slice(1);
      return <h2 className="title-3 mb-3">{capitalizedSubtitle}</h2>;
    }
    return null;
  }, [product.subtitle]);

  const reviews = useMemo(() => <Reviews id={product?.id?.split('/Product/')[1]} />, [product.id]);

  const description = useMemo(
    () =>
      product.description && (
        <div
          className="rte text-body-2 mb-[17px] lg:mb-5"
          dangerouslySetInnerHTML={{ __html: product.description }}
        ></div>
      ),
    [product.description],
  );

  const bundleChildren = useMemo(() => <BundleChildren bundle={bundle} openFlyout={openFlyout} />, [bundle]);

  const productQuantity = useMemo(
    () => (
      <ProductQuantity
        quantity={qty}
        changeQuantity={(value) => {
          setQty(value);
          trackPDPToggleClick(`quantity ${value}`);
        }}
        maxOrder={maxOrderQTY?.max_order_qty}
      />
    ),
    [qty],
  );

  const addToCartButton = useMemo(
    () => <AddToCartButton bundle={bundle} product={product} variant={variant} qty={qty} />,
    [bundle, product, variant, qty],
  );

  const addToCartSticky = useMemo(
    () => (
      <AddToCartSticky>
        <AddToCartButton bundle={bundle} product={product} variant={variant} qty={qty} />
      </AddToCartSticky>
    ),
    [bundle, product, variant, qty],
  );

  const keyDetailsWithFlyout = useMemo(() => <KeyDetailsWithFlyout detailsFlyout={detailsFlyout} />, [detailsFlyout]);

  useEffect(() => {
    const fetchData = async () => {
      setBundle(await getBundleProducts(product));
    };
    // eslint-disable-next-line no-console
    fetchData().catch(console.error);
  }, []);

  const shipping = useMemo(() => <Shipping freeShipping={freeShipping} />, [freeShipping]);

  const maxOrderMessage = useMemo(
    () => (
      <MaxOrderMessage maxOrder={maxOrderQTY?.max_order_qty} errorMessage={maxOrderQTY?.error_message} quantity={qty} />
    ),
    [maxOrderQTY, qty],
  );

  return (
    <form
      method="post"
      action="/cart/add"
      acceptCharset="UTF-8"
      encType="multipart/form-data"
      noValidate="novalidate"
      data-product-form
      className="product-bundle-form mt-5 lg:mt-0"
    >
      {bundle && title}
      {bundle && price}
      {bundle && subtitle}
      {bundle && reviews}
      {bundle && description}
      {bundle && bundleChildren}
      {bundle && (
        <div className="flex mt-[20px] mb-1.5">
          {productQuantity}
          <div ref={atcRef} className="w-full atc-wrapper">
            {addToCartButton}
          </div>
        </div>
      )}
      {maxOrderQTY && maxOrderMessage}
      {variant && <ShopPay price={variant.price} available={variant.availableForSale} />}
      {bundle && shipping}
      <ProductAppBlocks />
      {bundle && isMobile && !isInViewPort && addToCartSticky}
      <ProductDetails product={product} />
      {bundle && keyDetailsWithFlyout}
    </form>
  );
};

export default withProductContext(BundleForm);
